import { Fragment, useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';

import PropTypes from 'prop-types';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';

import { logout } from '../../../../actions/auth';
import { backendHOST } from '../../../../client-config/client-config';

import { PaymentMethods } from '../../../../utils/enums/userTypes';
import { withRouter } from '../../../hooks/withRouter';

const url = `${backendHOST}/api/scriptantiaAdmin`;
const urlEnd = '/TeacherPayments';

const emptyForm = {
  id_oktato: '',
  datum: '',
  osszeg: '',
  descr: '',
  fiz_mod: '',
};

const FormTeacherPayments = ({ auth }) => {
  const [payments, setPayments] = useState([]);
  const [teachers, setTeachers] = useState([]);

  const [searchText, setSearchText] = useState('');

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const authHeaders = {
    'Content-Type': 'application/json',
    'x-auth-token': localStorage.token,
  };

  const formatDateForInput = (value) => {
    if (!value) return '';

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return date.toISOString().slice(0, 10);
  };

  const numberOrNull = (value) => {
    if (value === '' || value === null || value === undefined) {
      return null;
    }

    return Number(value);
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [paymentsResponse, teachersResponse] = await Promise.all([
        fetch(`${url}${urlEnd}`, {
          headers: authHeaders,
        }),
        fetch(`${backendHOST}/api/auth/User?uType=T`, {
          headers: authHeaders,
        }),
      ]);

      if (!paymentsResponse.ok) {
        throw new Error('Nem sikerült betölteni az oktatói kifizetéseket.');
      }

      if (!teachersResponse.ok) {
        throw new Error('Nem sikerült betölteni az oktatókat.');
      }

      const paymentsData = await paymentsResponse.json();
      const teachersData = await teachersResponse.json();

      setPayments(Array.isArray(paymentsData) ? paymentsData : []);
      setTeachers(Array.isArray(teachersData) ? teachersData : []);
    } catch (err) {
      setError(err.message || 'Ismeretlen hiba történt betöltés közben.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTeacherLabel = (teacherId) => {
    const item = teachers.find(
      (teacher) => String(teacher.id) === String(teacherId),
    );

    return item?.text || teacherId || '';
  };

  const getPaymentMethodLabel = (paymentMethod) => {
    const item = PaymentMethods.find(
      (method) => String(method.key) === String(paymentMethod),
    );

    return item?.name || paymentMethod || '';
  };

  const sortedPayments = useMemo(() => {
    return [...payments].sort((a, b) => {
      const dateA = a.datum ? new Date(a.datum).getTime() : 0;
      const dateB = b.datum ? new Date(b.datum).getTime() : 0;

      return dateB - dateA;
    });
  }, [payments]);

  const filteredPayments = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    if (!normalizedSearch) {
      return sortedPayments;
    }

    return sortedPayments.filter((payment) =>
      [
        payment.id_bizonylat,
        payment.id_oktato,
        getTeacherLabel(payment.id_oktato),
        payment.datum,
        payment.osszeg,
        payment.descr,
        payment.fiz_mod,
        getPaymentMethodLabel(payment.fiz_mod),
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(normalizedSearch),
        ),
    );
  }, [sortedPayments, searchText, teachers]);

  const normalizeText = (value) =>
    String(value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const teacherOptions = useMemo(() => {
    return [...teachers]
      .map((teacher) => ({
        ...teacher,
        id: String(teacher.id ?? ''),
        text: String(teacher.text ?? ''),
        searchText: normalizeText(`${teacher.text ?? ''} ${teacher.id ?? ''}`),
      }))
      .filter((teacher) => teacher.id && teacher.text)
      .sort((a, b) =>
        a.text.localeCompare(b.text, 'hu', { sensitivity: 'base' }),
      );
  }, [teachers]);

  const handleCreateClick = () => {
    setFormData({
      ...emptyForm,
      datum: formatDateForInput(new Date()),
      fiz_mod: PaymentMethods?.[0]?.key || '',
    });

    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    if (saving) return;

    setDialogOpen(false);
    setFormData(emptyForm);
  };

  const handleFormChange = (field, value) => {
    setFormData((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const normalizePayload = (data) => {
    return {
      id_oktato: numberOrNull(data.id_oktato),
      datum: data.datum || null,
      osszeg: numberOrNull(data.osszeg),
      descr: data.descr || '',
      fiz_mod: data.fiz_mod || null,
    };
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const payload = normalizePayload(formData);

      const response = await fetch(`${url}${urlEnd}`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Nem sikerült rögzíteni az oktatói kifizetést.');
      }

      setMessage('Oktatói kifizetés rögzítve.');

      setDialogOpen(false);
      setFormData(emptyForm);

      await loadData();
    } catch (err) {
      setError(err.message || 'Mentés közben hiba történt.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Box sx={{ p: 2 }}>
        <Card elevation={2} sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent='space-between'
                spacing={2}
              >
                <Box>
                  <Typography variant='h6' sx={{ fontWeight: 600 }}>
                    Oktató bérek könyvelése
                  </Typography>

                  <Typography variant='body2' color='text.secondary'>
                    Oktatói kifizetések rögzítése és áttekintése.
                  </Typography>
                </Box>

                <Button
                  variant='contained'
                  startIcon={<AddIcon />}
                  onClick={handleCreateClick}
                >
                  Új oktatói kifizetés
                </Button>
              </Stack>

              <TextField
                label='Keresés'
                size='small'
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                sx={{ maxWidth: 320 }}
              />

              {error && (
                <Alert severity='error' onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant='outlined'>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>Oktató</TableCell>
                        <TableCell>Kifizetés dátuma</TableCell>
                        <TableCell align='right'>Összeg</TableCell>
                        <TableCell>Megjegyzés</TableCell>
                        <TableCell>Fizetési mód</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {filteredPayments.map((payment) => (
                        <TableRow key={payment.id_bizonylat} hover>
                          <TableCell>
                            {getTeacherLabel(payment.id_oktato)}
                          </TableCell>

                          <TableCell>
                            {formatDateForInput(payment.datum) || '-'}
                          </TableCell>

                          <TableCell align='right'>{payment.osszeg}</TableCell>

                          <TableCell>{payment.descr}</TableCell>

                          <TableCell>
                            {getPaymentMethodLabel(payment.fiz_mod)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {filteredPayments.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align='center'>
                            Nincs megjeleníthető oktatói kifizetés.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle>Új oktatói kifizetés</DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Autocomplete
              fullWidth
              options={teacherOptions}
              value={
                teacherOptions.find(
                  (teacher) =>
                    String(teacher.id) === String(formData.id_oktato),
                ) ?? null
              }
              onChange={(event, newValue) => {
                handleFormChange(
                  'id_oktato',
                  newValue ? String(newValue.id) : '',
                );
              }}
              getOptionLabel={(option) => option?.text ?? ''}
              isOptionEqualToValue={(option, value) =>
                String(option?.id) === String(value?.id)
              }
              filterOptions={(options, state) => {
                const search = normalizeText(state.inputValue);

                if (!search) {
                  return options.slice(0, 50);
                }

                return options
                  .filter((teacher) => teacher.searchText.includes(search))
                  .slice(0, 50);
              }}
              noOptionsText='Nincs találat'
              renderInput={(params) => (
                <TextField
                  {...params}
                  label='Oktató'
                  placeholder='Oktató neve vagy azonosítója...'
                />
              )}
            />

            <TextField
              label='Kifizetés dátuma'
              type='date'
              value={formData.datum}
              onChange={(event) =>
                handleFormChange('datum', event.target.value)
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              label='Összeg'
              type='number'
              value={formData.osszeg}
              onChange={(event) =>
                handleFormChange('osszeg', event.target.value)
              }
              fullWidth
            />

            <TextField
              label='Megjegyzés'
              value={formData.descr}
              onChange={(event) =>
                handleFormChange('descr', event.target.value)
              }
              multiline
              minRows={3}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel id='teacher-payment-method-label'>
                Fizetési mód
              </InputLabel>
              <Select
                labelId='teacher-payment-method-label'
                label='Fizetési mód'
                value={formData.fiz_mod}
                onChange={(event) =>
                  handleFormChange('fiz_mod', event.target.value)
                }
              >
                {PaymentMethods.map((method) => (
                  <MenuItem key={method.key} value={String(method.key)}>
                    {method.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleDialogClose} disabled={saving}>
            Mégsem
          </Button>

          <Button variant='contained' onClick={handleSave} disabled={saving}>
            {saving ? 'Mentés...' : 'Mentés'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(message)}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        message={message}
      />
    </Fragment>
  );
};

FormTeacherPayments.propTypes = {
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, { logout })(
  withRouter(FormTeacherPayments),
);
